import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChefHat, Menu, X, User, ShoppingCart, MapPin, ChevronDown, Loader, Navigation } from 'lucide-react';
import '../Header/Header.css';
import NotificationBell from '../Notifications/NotificationBell';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { CART_COUNT_URL, GET_SAVED_ADDRESSES_URL, SET_ACTIVE_ADDRESS_URL, UPDATE_CURRENT_LOCATION_URL } from '../../config/api';
import { getCurrentLocationDetails, storeCoords } from '../Utils/location';

interface HeaderProps {
  isLoggedIn: boolean;
  onLogout: () => void;
  hideAuthButtons?: boolean;
  onCartClick?: () => void;
  cartRefreshToken?: number;
}

interface SavedAddress {
  address_id: string;
  label: string;
  address_line: string;
  city: string;
  state: string;
  is_default: boolean;
}

const Header: React.FC<HeaderProps> = ({
  isLoggedIn,
  onLogout,
  hideAuthButtons = false,
  onCartClick,
  cartRefreshToken = 0,
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [activeCity, setActiveCity] = useState(() => localStorage.getItem('activeCity') || '');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [settingAddressId, setSettingAddressId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchCartCount = useCallback(async () => {
    if (!isLoggedIn) { setCartCount(0); return; }
    try {
      const res = await authenticatedFetch(CART_COUNT_URL);
      if (res.ok) {
        const json = await res.json();
        setCartCount(json.count ?? 0);
      }
    } catch {
      // non-critical
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchCartCount();
  }, [fetchCartCount, cartRefreshToken]);

  useEffect(() => {
    const handler = () => setActiveCity(localStorage.getItem('activeCity') || '');
    window.addEventListener('activeCityChanged', handler);
    return () => window.removeEventListener('activeCityChanged', handler);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const loadAddresses = useCallback(async () => {
    if (!isLoggedIn) return;
    setAddressesLoading(true);
    try {
      const res = await authenticatedFetch(GET_SAVED_ADDRESSES_URL);
      if (res.ok) {
        const json = await res.json();
        setSavedAddresses(json.saved_addresses ?? []);
      }
    } catch {
      // non-critical
    } finally {
      setAddressesLoading(false);
    }
  }, [isLoggedIn]);

  const handleChipClick = () => {
    if (!dropdownOpen) loadAddresses();
    setDropdownOpen(prev => !prev);
  };

  const handleSetActive = async (address: SavedAddress) => {
    setSettingAddressId(address.address_id);
    try {
      const res = await authenticatedFetch(SET_ACTIVE_ADDRESS_URL(address.address_id), {
        method: 'PUT',
        headers: { accept: 'application/json' },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        const city = json.active_address?.city || address.city;
        localStorage.setItem('activeCity', city.trim());
        window.dispatchEvent(new Event('activeCityChanged'));
        setActiveCity(city.trim());
      }
    } catch {
      // non-critical
    } finally {
      setSettingAddressId(null);
      setDropdownOpen(false);
    }
  };

  const handleDetectLocation = async () => {
    setDetectingLocation(true);
    try {
      const result = await getCurrentLocationDetails();
      if ('type' in result) { setDetectingLocation(false); return; }
      storeCoords(result.latitude, result.longitude);
      localStorage.setItem('activeCity', result.city.trim());
      window.dispatchEvent(new Event('activeCityChanged'));
      setActiveCity(result.city.trim());
      await authenticatedFetch(
        UPDATE_CURRENT_LOCATION_URL(result.latitude, result.longitude, result.full_address),
        { method: 'POST', headers: { accept: 'application/json' } }
      );
    } catch {
      // non-critical
    } finally {
      setDetectingLocation(false);
      setDropdownOpen(false);
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleSignOut = () => {
    onLogout();
    navigate('/', { replace: true });
  };

  return (
    <header className="header-container">
      <div className="header-left-group">
        <Link to="/" className="logo">
          <ChefHat size={24} />
          <span>HomeCookt</span>
        </Link>

        {isLoggedIn && (
          <div className="header-location-wrap" ref={dropdownRef}>
            <button
              className={`header-city-chip${dropdownOpen ? ' open' : ''}`}
              onClick={handleChipClick}
              title="Change delivery location"
            >
              <MapPin size={13} />
              <span className="header-city-text">{activeCity || 'Set location'}</span>
              <ChevronDown size={12} className={`header-city-chevron${dropdownOpen ? ' rotated' : ''}`} />
            </button>

            {dropdownOpen && (
              <div className="header-loc-dropdown">
                <div className="hld-section">
                  <p className="hld-section-title">Current location</p>
                  <button
                    className="hld-detect-btn"
                    onClick={handleDetectLocation}
                    disabled={detectingLocation}
                  >
                    {detectingLocation
                      ? <><Loader size={13} className="hld-spinner" /> Detecting...</>
                      : <><Navigation size={13} /> Detect my location</>
                    }
                  </button>
                </div>

                {savedAddresses.length > 0 && (
                  <div className="hld-section">
                    <p className="hld-section-title">Saved addresses</p>
                    {addressesLoading ? (
                      <div className="hld-loading"><Loader size={14} className="hld-spinner" /></div>
                    ) : (
                      <ul className="hld-address-list">
                        {savedAddresses.map(addr => (
                          <li key={addr.address_id} className="hld-address-item">
                            <div className="hld-address-info">
                              <span className="hld-address-label">{addr.label}</span>
                              <span className="hld-address-city">{addr.city}</span>
                            </div>
                            <button
                              className="hld-set-btn"
                              onClick={() => handleSetActive(addr)}
                              disabled={settingAddressId === addr.address_id}
                            >
                              {settingAddressId === addr.address_id
                                ? <Loader size={12} className="hld-spinner" />
                                : 'Deliver here'
                              }
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="hld-footer">
                  <button
                    className="hld-manage-btn"
                    onClick={() => { setDropdownOpen(false); navigate('/profile?tab=addresses'); }}
                  >
                    Manage addresses
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <button className="menu-button" onClick={toggleMenu}>
        <Menu size={24} />
      </button>

      <nav className={`nav ${isMenuOpen ? 'open' : ''}`}>
        <button className="close-button" onClick={toggleMenu}>
          <X size={24} />
        </button>

        <ul className="nav-list">
          <li className="nav-item">
            <Link to={isLoggedIn ? "/home" : "/"} onClick={() => setIsMenuOpen(false)}>
              Home
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/about" onClick={() => setIsMenuOpen(false)}>About Us</Link>
          </li>
          <li className="nav-item">
            <a href="https://wa.me/447737024736" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)}>Contact</a>
          </li>

          {!hideAuthButtons && !isLoggedIn ? (
            <>
              <li className="nav-item">
                <Link
                  to="/login"
                  className="auth-button1 login"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Log In
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  to="/signup"
                  className="auth-button1 signup"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </li>
            </>
          ) : !hideAuthButtons ? (
            <>
              <li className="nav-item">
                <NotificationBell />
              </li>
              {onCartClick && (
                <li className="nav-item">
                  <button
                    className="cart-nav-btn"
                    onClick={() => { onCartClick(); setIsMenuOpen(false); }}
                    aria-label="Open cart"
                  >
                    <ShoppingCart size={18} />
                    {cartCount > 0 && (
                      <span className="cart-nav-badge">{cartCount > 99 ? '99+' : cartCount}</span>
                    )}
                  </button>
                </li>
              )}
              <li className="nav-item">
                <Link
                  to="/profile"
                  className="profile-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User size={16} />
                  Profile
                </Link>
              </li>
              <li className="nav-item">
                <button
                  className="auth-button1 login"
                  onClick={() => {
                    handleSignOut();
                    setIsMenuOpen(false);
                  }}
                >
                  Sign Out
                </button>
              </li>
            </>
          ) : (
            <li className="nav-item">
              <button
                className="auth-button1 login"
                onClick={() => {
                  onLogout();
                  setIsMenuOpen(false);
                }}
              >
                Sign Out
              </button>
            </li>
          )}
        </ul>
      </nav>
    </header>
  );
};

export default Header;
